import { DoorsEdit } from "company/detail/components/DoorsEdit";
import { getUpdateVenueInstuction, DoorsFormState, VenueFormState, CompanyFormState, CompanyFieldMetaData } from "shared/utils/fieldsHelper/companyFields";
import { StageEdit } from "company/detail/components/StageEdit";
import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import VenueCategoryDropdown from "components/dropdowns/reactselect/VenueCategoryDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React from "react";
import Doors from "venue/components/Doors";
import Stage from "venue/components/Stage";

export const venueDetailsSection = (
  companyFormState: CompanyFormState,
  venueFormState: VenueFormState,
  fieldMetaData: CompanyFieldMetaData
): FormSection => {
  return {
    type: "collapsable",
    title: site.currentEnvironment.functionGroups.venue.singular,
    key: "companyVenueDetails",
    hidden: companyFormState.profile.isVenue === "no",
    fields: [{
      ...fieldMetaData.capacity.fields(),
      key: "capacity",
      label: RESX.Venue.Capacity,
      render: () => <div>{venueFormState.capacity} PAX</div>,
      inPageEdit: {
        initialValue: venueFormState.capacity,
        render: ({ value, onChange, cancel }) => (
          <OptionalNumberInput
            onEnterPressed={(e) => {
              onChange(e, true);
            }}
            onEscPressed={cancel}
            autoFocus
            value={value}
            onChange={onChange}
            minValue={0}
            maxValue={serverReference.constants.venue.maxCapacity}
            canBeEmpty
            decimalPoints={0}
            labelContent="PAX"
            width={100}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: number) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { capacity: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.ageRestriction.fields(),
      key: "ageRestriction",
      label: RESX.Venue.AgeRestrictions,
      render: () => <div>{venueFormState.ageRestriction.text}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.ageRestriction,
        render: ({ value, onChange }) => (
          <AgeRestrictionDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.AgeRestriction) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { ageRestriction: value.value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      key: "doors",
      label: RESX.Venue.Doors,
      render: () => <Doors doors={venueFormState.doors} />,
      isEmpty: !venueFormState.doors.open && !venueFormState.doors.close,
      inPageEdit: {
        initialValue: venueFormState.doors,
        render: ({ value, onChange, }) => (
          <DoorsEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: DoorsFormState) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, {
            doors: {
              open: value.open?.format("HH:mm"),
              close: value.close?.format("HH:mm")
            }
          });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.category.fields(),
      key: "category",
      label: RESX.Venue.Category,
      render: () => <div>{venueFormState.category.text || ""}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.category,
        render: ({ value, onChange }) => (
          <VenueCategoryDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.VenueCategory) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { category: value.value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.stage.fields(),
      key: "stage",
      label: RESX.Venue.Stage,
      render: () => <Stage stage={venueFormState.stage} />,
      inPageEdit: {
        initialValue: venueFormState.stage,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <div className="main-section-template-fields-padding">
            <StageEdit
              stage={value}
              onChange={onChange}
              submit={submit}
              isSaving={isSaving}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.Stage) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { stage: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.vehicleAccess.fields(),
      key: "vehicleAccess",
      label: RESX.Venue.VehicleAccess,
      render: () => <div>{venueFormState.vehicleAccess}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.vehicleAccess,
        render: ({ value, onChange, cancel, submit }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxVehicleAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { vehicleAccess: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.parking.fields(),
      key: "parking",
      label: RESX.Venue.Parking,
      render: () => <div>{venueFormState.parking}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.parking,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxParkingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { parking: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }

    }, {
      ...fieldMetaData.gearAccess.fields(),
      key: "gearAccess",
      label: RESX.Venue.GearAccess,
      render: () => <div>{venueFormState.gearAccess}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.gearAccess,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxGearAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { gearAccess: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.power.fields(),
      key: "power",
      label: RESX.Venue.Power,
      render: () => <div>{venueFormState.power}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.power,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxPowerLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { power: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.lighting.fields(),
      key: "lighting",
      label: RESX.Venue.Lighting,
      render: () => <div>{venueFormState.lighting}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.lighting,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxLightingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { lighting: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.dressingRooms.fields(),
      key: "dressingRooms",
      label: RESX.Venue.DressingRooms,
      render: () => <div>{venueFormState.dressingRooms}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.dressingRooms,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxDressingRoomsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { dressingRooms: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.noiseRestrictions.fields(),
      key: "noiseRestrictions",
      label: RESX.Venue.NoiseRestrictions,
      render: () => <div>{venueFormState.noiseRestrictions}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.noiseRestrictions,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxNoiseRestrictionsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(companyFormState.companyId, venueFormState, { noiseRestrictions: value });
          return window.CoreApi.Venue.Update(instruction);
        }
      }
    }],
  }
}